<template>
  <div class="content-data text-article">
    <component :is="contentHtmlTag" v-if="renderType === RenderTypeEnum.COMMON" ref="contentRef">
      <div v-html="props.meta"></div>
    </component>
    <!-- currently only have ul, ol tags -->
    <component :is="contentHtmlTag" v-else-if="renderType === RenderTypeEnum.WITH_CHILDREN" ref="contentRef">
      <li v-for="(data, index) in props.list" :key="index" v-html="data"></li>
    </component>
    <component
      :is="contentHtmlTag"
      v-else-if="renderType === RenderTypeEnum.MEDIA"
      ref="contentRef"
      v-bind="mediaAttrs"
      @click="onMediaClick(mediaAttrs)"
    />
  </div>
</template>

<script setup lang="ts">
// ref: funnow.web.nuxt /components/ContentData.vue
import type { ProductContentData } from '@/types/product'
import { ProductDetailHtmlTypeEnum } from '@/types/product'

enum RenderTypeEnum {
  COMMON = 1,
  WITH_CHILDREN = 2,
  MEDIA = 3,
}

const props = withDefaults(defineProps<ProductContentData>(), {
  type: ProductDetailHtmlTypeEnum.HTML,
  meta: '',
  list: () => [],
  mediaWidth: 0,
  mediaHeight: 0,
})

const emit = defineEmits<{
  (event: 'image-click', src: string): void
}>()

const contentRef = ref(null)

const { stop: stopObserveMedias } = useIntersectionObserver(
  contentRef,
  entries => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) {
        ;(entry.target as HTMLIFrameElement).contentWindow?.postMessage(
          JSON.stringify({ event: 'command', func: 'pauseVideo' }),
          'https://www.youtube.com'
        )
      }
    })
  },
  {
    rootMargin: '0px',
    threshold: [0],
  }
)

const contentHtmlTag = computed(() => {
  switch (props.type) {
    case ProductDetailHtmlTypeEnum.HTML:
      return 'div'
    case ProductDetailHtmlTypeEnum.IMAGE:
      return 'img'
    case ProductDetailHtmlTypeEnum.YOUTUBE:
      return 'iframe'
    case ProductDetailHtmlTypeEnum.UNORDERED_LIST:
      return 'ul'
    case ProductDetailHtmlTypeEnum.ORDERED_LIST:
      return 'ol'
    default:
      return 'div'
  }
})

const renderType = computed(() => {
  switch (props.type) {
    case ProductDetailHtmlTypeEnum.HTML:
      return RenderTypeEnum.COMMON
    case ProductDetailHtmlTypeEnum.IMAGE:
    case ProductDetailHtmlTypeEnum.YOUTUBE:
      return RenderTypeEnum.MEDIA
    case ProductDetailHtmlTypeEnum.ORDERED_LIST:
    case ProductDetailHtmlTypeEnum.UNORDERED_LIST:
      return RenderTypeEnum.WITH_CHILDREN
    default:
      return RenderTypeEnum.COMMON
  }
})

const mediaAttrs = computed(() => {
  const attrs: { src: string; class: string; style: Record<string, any>; [key: string]: any } = {
    src: '',
    class: '',
    style: {},
  }
  switch (props.type) {
    case ProductDetailHtmlTypeEnum.IMAGE:
      attrs.src = props.meta
      attrs.class = 'w-full cursor-pointer'
      attrs.style = {
        ...(Boolean(props.mediaHeight) && { 'aspect-ratio': `${props.mediaWidth}/${props.mediaHeight}` }),
      }
      break
    case ProductDetailHtmlTypeEnum.YOUTUBE:
      attrs.src = `https://www.youtube.com/embed/${props.meta}?enablejsapi=1`
      attrs.class = 'w-full aspect-video'
      attrs.allow = 'fullscreen'
      attrs.loading = 'lazy'
      attrs.referrerpolicy = 'strict-origin-when-cross-origin'
      break
  }

  return attrs
})

onUnmounted(() => {
  stopObserveMedias()
})

const onMediaClick = ({ src }: { src: string }) => {
  if (props.type === ProductDetailHtmlTypeEnum.IMAGE) {
    emit('image-click', src)
  }
}
</script>

<style lang="scss" scoped></style>
